// @ts-nocheck
import { Verification } from '../../requests/verifications';
import { formatBusinessName } from '../../utils/nameFormatters';
import { isRowClickable } from '../../utils/verificationAdmin';
import { Colors } from '../../styles/colors';
import React from 'react';
import {
    Typography,
    Avatar,
} from '@mui/material';
import {
    Check as CheckIcon
} from '@mui/icons-material';
import {
    PiBuildingsLight,
    PiWarningCircle,
    PiWarningDiamondThin
} from 'react-icons/pi';

// Define column type
export type GridColumnDef = {
    field: string;
    headerName: string;
    flex?: number;
    width?: number;
    sortable?: boolean;
    renderCell?: (params: any) => React.ReactNode;
    sortComparator?: (v1: any, v2: any, param1: any, param2: any) => number;
};

// Risk level options for multi-select
export const riskLevels = [
    'Low risk',
    'Pending',
    'Risk warnings',
    'High risk',
    'No match',
    'Failed',
];

// Helper function to determine risk level
export const getRiskLevel = (verification: Verification) => {
    const status = (verification.status || '').toLowerCase();

    // Handle specific backend statuses
    switch (status) {
        case 'failed':
            return 'failed';
        case 'no_match':
            return 'no_match';
        case 'created':
        case 'pending':
        case 'running':
        case 'ready_for_review':
            return 'pending';
        case 'completed':
            if (verification.risk_score < 0.3) return 'low_risk';
            if (verification.risk_score > 0.6) return 'high_risk';
            return 'risk_warnings';
        default:
            return 'pending';
    }
};

// Helper function to get risk display info
export const getRiskDisplayInfo = (riskLevel: string) => {
    switch (riskLevel) {
        case 'low_risk':
            return {
                text: 'Low risk',
                color: Colors.TABLE_LOW_RISK,
            };
        case 'high_risk':
            return {
                text: 'High risk',
                color: Colors.TABLE_HIGH_RISK,
            };
        case 'pending':
            return {
                text: 'Pending',
                color: Colors.NEUTRAL_GRAY,
            };
        case 'risk_warnings':
            return {
                text: 'Risk warnings',
                color: Colors.TABLE_RISK_WARNINGS,
            };
        case 'no_match':
            return {
                text: 'No match',
                color: Colors.NO_MATCH_PINK,
            };
        case 'failed':
            return {
                text: 'Failed',
                color: Colors.TABLE_HIGH_RISK,
            };
        default:
            return {
                text: 'Unknown',
                color: Colors.NEUTRAL_GRAY,
            };
    }
};

// Format date for display
export const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    };
    return date.toLocaleDateString('en-US', options).replace(/,(?!.*,)/, ' -').replace(/ at /, ' - ');
};

// Filter verifications based on search, date, and risk filters
export const filterVerifications = (
    verifications: Verification[],
    searchTerm: string,
    dateFilter: string,
    selectedRisks: string[],
    isAdmin: boolean
) => {
    return verifications.filter(verification => {
        // Search filter
        const searchMatch = verification.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? true;

        // Date filter
        let dateMatch = true;
        if (dateFilter && dateFilter.trim()) {
            // Parse the date range from the filter string (format: "MM/DD/YYYY - MM/DD/YYYY")
            const dateRangeParts = dateFilter.split(' - ');
            if (dateRangeParts.length === 2) {
                const startDate = new Date(dateRangeParts[0]);
                const endDate = new Date(dateRangeParts[1]);
                const verificationDate = new Date(verification.created_at);

                // Set end date to end of day for inclusive comparison
                endDate.setHours(23, 59, 59, 999);

                // Check if verification date is within the selected range
                if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime()) && !isNaN(verificationDate.getTime())) {
                    dateMatch = verificationDate >= startDate && verificationDate <= endDate;
                }
            }
        }

        // Risk filter
        let riskMatch = true;
        if (selectedRisks.length > 0) {
            const riskLevel = getRiskLevel(verification);
            const displayInfo = getRiskDisplayInfo(riskLevel);
            riskMatch = selectedRisks.includes(displayInfo.text);
        }

        return searchMatch && dateMatch && riskMatch;
    });
};

// Function to get sortable value for result column
export const getResultSortValue = (verification: Verification): string => {
    const riskLevel = getRiskLevel(verification);
    const displayInfo = getRiskDisplayInfo(riskLevel);
    return displayInfo.text;
};

// Create columns definition function
export const createColumns = (
    navigate: (path: string) => void,
    StyledBusinessCell: any,
    StyledRiskCell: any,
    StyledViewDetailsButton: any,
    isAdmin: boolean
): GridColumnDef[] => {
    return [
        {
            field: 'business_name',
            headerName: 'Clients',
            flex: 1,
            renderCell: (params) => {
                return React.createElement(StyledBusinessCell, {
                        sx: { paddingLeft: 1 }
                    },
                    React.createElement(Avatar, {
                        sx: {
                            width: 24,
                            height: 24,
                            backgroundColor: Colors.BRAND_BLUE,
                        }
                    }, React.createElement(PiBuildingsLight, { size: 16, color: "white" })),
                    React.createElement(Typography, {
                        variant: "body2",
                        sx: { color: Colors.PRIMARY_DARK, fontWeight: 400 }
                    }, formatBusinessName(params.value || 'Unnamed Business'))
                );
            },
        },
        {
            field: 'created_at',
            headerName: 'Date added',
            flex: 1,
            renderCell: (params) => {
                return React.createElement(Typography, {
                    variant: "body2",
                    sx: { color: Colors.PRIMARY_DARK }
                }, formatDate(params.value));
            },
        },
        {
            field: 'result',
            headerName: 'Result',
            flex: 1,
            sortComparator: (v1: any, v2: any, param1: any, param2: any) => {
                // Sort based on the displayed risk level text, not the raw field value
                const text1 = getResultSortValue(param1.row);
                const text2 = getResultSortValue(param2.row);
                return text1.localeCompare(text2);
            },
            renderCell: (params) => {
                const riskLevel = getRiskLevel(params.row);
                const displayInfo = getRiskDisplayInfo(riskLevel);

                // Render the appropriate icon based on risk level
                const getIcon = (): React.ReactNode => {
                    switch (riskLevel) {
                        case 'low_risk':
                            return React.createElement(CheckIcon, {
                                sx: { fontSize: 16, color: Colors.TABLE_LOW_RISK, mr: 0.5 }
                            });
                        case 'risk_warnings':
                            return React.createElement(PiWarningCircle, {
                                size: 16,
                                color: Colors.TABLE_RISK_WARNINGS,
                                style: { marginRight: 4 }
                            });
                        case 'high_risk':
                            return React.createElement(PiWarningCircle, {
                                size: 16,
                                color: Colors.TABLE_HIGH_RISK,
                                style: { marginRight: 4 }
                            });
                        case 'no_match':
                            return React.createElement(PiWarningDiamondThin, {
                                size: 16,
                                color: Colors.NO_MATCH_PINK,
                                style: { marginRight: 4 }
                            });
                        case 'failed':
                            return React.createElement(PiWarningCircle, {
                                size: 16,
                                color: Colors.TABLE_HIGH_RISK,
                                style: { marginRight: 4 }
                            });
                        default:
                            return null;
                    }
                };

                const icon = getIcon();

                return React.createElement(StyledRiskCell, {},
                    icon,
                    React.createElement(Typography, {
                        variant: "body2",
                        sx: { color: displayInfo.color, fontWeight: 500 }
                    }, displayInfo.text)
                );
            },
        },
        {
            field: 'actions',
            headerName: '',
            width: 120,
            sortable: false,
            renderCell: (params) => {
                if (!isRowClickable(params.row.status, isAdmin)) return null;

                return React.createElement('a', {
                    href: `/verifications/${params.row.id}`,
                    target: '_blank',
                    rel: 'noopener noreferrer',
                    style: { textDecoration: 'none' },
                    onClick: (e) => {
                        e.stopPropagation();
                        // Only open in new tab if command/ctrl key is held
                        if (!e.metaKey && !e.ctrlKey) {
                            e.preventDefault();
                            window.location.href = `/verifications/${params.row.id}`;
                        }
                    }
                }, React.createElement(StyledViewDetailsButton, {
                    variant: "outlined",
                    size: "small",
                    component: "span"
                }, "View details"));
            },
        },
    ];
};
